// src/components/ProductsPage/Productspage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Productspage.scss";
import PageBanner from "../layouts/PageBanner";
import aboutProducts from "../../assets/about-hero.jpg";

const API = "https://api.ainfotech.com.np";

const Productspage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // group == category slug
  const qGroup = (params.get("group") || "").toLowerCase();
  const qCat = (params.get("cat") || "").toLowerCase(); // kept for compatibility (unused)

  const [cats, setCats] = useState([]);       // [{id,name,slug,description}]
  const [items, setItems] = useState([]);     // items for current group
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);

  // Load categories once
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/products/categories`);
        const data = await res.json();
        setCats(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setCats([]);
      } finally {
        setLoadingCats(false);
      }
    })();
  }, []);

  // Load items for selected group (category slug)
  useEffect(() => {
    if (!qGroup) return;
    (async () => {
      try {
        setLoadingItems(true);
        setItems([]);
        const res = await fetch(`${API}/api/products/category/${qGroup}/items?status=published`);
        const data = await res.json();
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (e) {
        console.error(e);
        setItems([]);
      } finally {
        setLoadingItems(false);
      }
    })();
  }, [qGroup]);

  const section = useMemo(() => {
    if (!qGroup) return null;
    return cats.find(c => (c.slug || "").toLowerCase() === qGroup) || null;
  }, [cats, qGroup]);

  const bannerTitle = section ? section.name : "Products";

  // Landing cards (categories)
  const categories = useMemo(() => {
    return cats.map((c) => ({
      key: c.slug,
      title: c.name,
      blurb: c.description,
      img: aboutProducts, // categories have no image; use fallback
    }));
  }, [cats]);

  // Items to show (we keep ?cat compatibility – if present, filter for that slug)
  const itemsToShow = useMemo(() => {
    if (!section) return [];
    if (qCat) {
      const one = items.find((it) => (it.slug || "").toLowerCase() === qCat);
      return one ? [one] : [];
    }
    return items;
  }, [section, qCat, items]);

  const gotoDetail = (groupSlug, itemSlug) => {
    navigate(`/products/${groupSlug}/${itemSlug}`);
  };

  const goGroup = (groupSlug) => {
    navigate(`/productpage?group=${encodeURIComponent(groupSlug)}`);
  };

  return (
    <div className="avena-products-page">
      <PageBanner
        title={bannerTitle}
        bg={aboutProducts}
        crumbs={[
          { label: "Avena", href: "/" },
          { label: "Products" },
          ...(section ? [{ label: section.name }] : []),
        ]}
      />

      <section className="products-switch">
        <div className="container">
          {/* ===== Landing: show categories if no group selected ===== */}
          {!qGroup && (
            <>
              <header className="prod-head mt-3">
                <h2 className="prod-title">Browse by Category</h2>
                <p className="prod-blurb">Choose a category to view products.</p>
              </header>

              {loadingCats ? (
                <div>Loading…</div>
              ) : (
                <div className="prod-groups">
                  {categories.map((cat) => (
                    <article
                      key={cat.key}
                      className="prod-card"
                      onClick={() => goGroup(cat.key)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open ${cat.title}`}
                    >
                      <div
                        className="prod-media"
                        style={{ backgroundImage: `url(${cat.img})` }}
                      />
                      <div className="prod-info">
                        <div className="prod-titlebox" role="heading" aria-level={3}>
                          <h3 className="prod-name">{cat.title}</h3>
                        </div>
                        {cat.blurb && <p className="prod-text">{cat.blurb}</p>}
                        <button
                          className="prod-read"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            goGroup(cat.key);
                          }}
                        >
                          <span className="pipe" aria-hidden="true">|</span> VIEW
                        </button>
                      </div>
                    </article>
                  ))}
                  {categories.length === 0 && <div>No categories found.</div>}
                </div>
              )}
            </>
          )}

          {/* ===== Group view (with optional single-item via ?cat=) ===== */}
          {qGroup && section && (
            <>
              {loadingItems ? (
                <div>Loading…</div>
              ) : (
                <div className="prod-grid">
                  {itemsToShow.map((it) => {
                    const img = it.cover_image ? `${API}${it.cover_image}` : aboutProducts;
                    return (
                      <article
                        className="prod-card"
                        key={it.slug}
                        onClick={() => gotoDetail(qGroup, it.slug)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open ${it.title}`}
                      >
                        <div
                          className="prod-media"
                          style={{ backgroundImage: `url(${img})` }}
                        />
                        <div className="prod-info">
                          <div className="prod-titlebox" role="heading" aria-level={3}>
                            <h3 className="prod-name">{it.title}</h3>
                          </div>
                          <p className="prod-text">
                            {it.excerpt || "Reliable, scalable solutions tailored to your environment."}
                          </p>
                          <button
                            className="prod-read"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              gotoDetail(qGroup, it.slug);
                            }}
                          >
                            <span className="pipe" aria-hidden="true">|</span> READ MORE
                          </button>
                        </div>
                      </article>
                    );
                  })}
                  {itemsToShow.length === 0 && (
                    <div>No products published in this category yet.</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Productspage;
